import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { When, If, Then, Else } from 'react-if';
import { ReactComponent as PopupIcon } from './PopupIcon.svg';
import './style.css';
import { createTeam, getUserTeams, updateTeam, removeTeam } from '../../../../firebase/api/teams';
import { getUserUid } from '../../../../static/localStorage';
import Select from '../../../../components/form/select';
import { ReactComponent as RemoveIcon } from './remove-icon.svg';
import { ReactComponent as PlusIcon } from './plus-icon.svg';

const teamDto = {
    ownerId: null,
    teamName: '',
    professionDescription: '',
    invitedUsers: [],
};

const PRO_TEAMS_NOTIFICATION_LENGTH = 3;

const MyTeamsModal = ({ modal, toggle }) => {
    const [isLoading, setLoadingState] = useState(true);
    const [teams, setTeams] = useState([]);

    useEffect(() => {
        const userId = getUserUid();
        getUserTeams(userId).then(teams => {
            setLoadingState(false);
            setTeams(teams);
        });
    }, []);

    useEffect(() => {
        teams.forEach(t => {
            updateTeam(t);
        });
    }, [teams]);

    const handleRemoveTeamButtonClick = useCallback(
        teamId => async () => {
            setTeams(teams.filter(team => team.teamId !== teamId));
            await removeTeam(teamId);
        },
        [teams]
    );

    const handleTeamInputChange = useCallback(
        teamId => e => {
            const teamIndex = teams.findIndex(t => t.teamId === teamId);
            const newTeams = [...teams];
            newTeams[teamIndex][e.target.name] = e.target.value;

            setTeams(newTeams);
        },
        [teams]
    );

    const handleOptionAdd = useCallback(
        (value, teamId) => {
            const teamIndex = teams.findIndex(t => t.teamId === teamId);
            const newTeams = [...teams];
            newTeams[teamIndex].invitedUsers = [value, ...newTeams[teamIndex].invitedUsers];

            setTeams(newTeams);
        },
        [teams]
    );

    const handleOptionRemove = useCallback(
        (value, teamId) => {
            const teamIndex = teams.findIndex(t => t.teamId === teamId);
            const newTeams = [...teams];
            newTeams[teamIndex].invitedUsers = newTeams[teamIndex].invitedUsers.filter(
                team => team !== value
            );

            setTeams(newTeams);
        },
        [teams]
    );

    const createNewTeam = useCallback(async () => {
        const userId = getUserUid();
        const newTeam = { ...teamDto, ownerId: userId };

        await createTeam(newTeam);
        setTeams([...teams, newTeam]);
    }, [teams]);

    return (
        <Modal isOpen={modal} toggle={toggle} className="my-teams-modal">
            <ModalHeader toggle={toggle}>
                <PopupIcon />
                Create your <b className="ml-1">dream teams</b>
            </ModalHeader>
            <ModalBody className="my-teams-modal__container">
                <Container fluid>
                    <Row>
                        <Col sm={12} md={6} lg={{ size: 3, offset: 2 }}>
                            <h6 className="header">Team Name</h6>
                        </Col>
                        <Col sm={12} md={6} lg={3}>
                            <h6 className="header">Profession description</h6>
                        </Col>
                        <Col sm={12} md={6} lg={4}>
                            <h6 className="header">Add Email addresses</h6>
                        </Col>
                        <Col lg={2} />
                    </Row>
                </Container>
                {isLoading ? (
                    <div>loading</div>
                ) : (
                    <Container fluid>
                        {teams.map((team, index) => (
                            <Row
                                key={team.teamId}
                                className="align-items-center justify-content-center"
                            >
                                <Col sm={12} md={6} lg={1}>
                                    <div className="cursor-pointer">
                                        <RemoveIcon
                                            onClick={handleRemoveTeamButtonClick(team.teamId)}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={6} lg={3}>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="teamName"
                                            className="form-control"
                                            placeholder="Team Name"
                                            value={team.teamName}
                                            onChange={handleTeamInputChange(team.teamId)}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={6} lg={3}>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="professionDescription"
                                            className="form-control"
                                            placeholder="Profession description"
                                            value={team.professionDescription}
                                            onChange={handleTeamInputChange(team.teamId)}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={6} lg={4}>
                                    <div className="form-group">
                                        <Select
                                            id={team.teamId}
                                            data={team.invitedUsers}
                                            onOptionAdd={handleOptionAdd}
                                            onOptionRemove={handleOptionRemove}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        ))}
                    </Container>
                )}
                <When condition={teams?.length < PRO_TEAMS_NOTIFICATION_LENGTH}>
                    <Container fluid>
                        <Row className="align-items-center justify-content-center">
                            <Col sm={12} md={6} lg={1}>
                                <div className="cursor-pointer mr-1">
                                    <PlusIcon onClick={createNewTeam} />
                                </div>
                            </Col>
                            <If condition={!!teams?.length}>
                                <Then>
                                    <Col sm={12} md={6} lg={3}>
                                        <div className="my-teams-modal__placeholder" />
                                    </Col>
                                    <Col sm={12} md={6} lg={3}>
                                        <div className="my-teams-modal__placeholder" />
                                    </Col>
                                    <Col sm={12} md={6} lg={4}>
                                        <div className="my-teams-modal__placeholder" />
                                    </Col>
                                </Then>
                                <Else>
                                    <Col sm={12} md={6} lg={10}>
                                        <div className="my-teams-modal__placeholder">
                                            Click to the plus button to create your first team
                                        </div>
                                    </Col>
                                </Else>
                            </If>
                        </Row>
                    </Container>
                </When>
            </ModalBody>
            <When condition={teams?.length >= PRO_TEAMS_NOTIFICATION_LENGTH}>
                <div className="align-items-center d-flex footer__pro-label px-4">
                    <span>
                        When our Pro Version is available, you will be able to create up to 8 teams.
                    </span>
                </div>
            </When>
        </Modal>
    );
};

export default MyTeamsModal;
